import React, { useState } from "react";
import { StockItem, StockItemGroupStats } from "../models/models";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useAppDispatch } from "../../../shared/hooks/sharedHooks";
import { moveStockItemsToGroup } from "../customerInventorySlice";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import useTranslation from "../../../shared/hooks/useTranslation";

export interface MoveStockItemDialogProps {
  stockItems: StockItem[];
  groups: StockItemGroupStats[];
  groupName: string;
  open: boolean;
  onClose: () => void;
  onConfirm: (value?: string) => void;
}

const MoveStockItemDialog = ({ stockItems, groups, groupName, open, onClose, onConfirm }: MoveStockItemDialogProps) => {
  const dispatch = useAppDispatch();
  const confirm = useConfirmation();
  const [groupId, setGroupId] = useState(groups[0]?.id);
  const { translate: tInventory } = useTranslation("inventory");
  const { translate: tCommon } = useTranslation("common");

  const handleGroupId = (value: string) => {
    setGroupId(value);
  };

  const onSubmit = async () => {
    const ids = stockItems.map((x) => x.id);
    await dispatch(moveStockItemsToGroup({ groupId: groupId, ids: ids }));
    if (ids.length === 1) {
      onConfirm(ids[0]);
    } else {
      onConfirm();
    }
  };


  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        <p className="heading-2">{tInventory("moveToFolder", { count: stockItems.length })}</p>
      </DialogTitle>

      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 14,
          top: 14,
          color: "#333333"
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Box>
          <p className="text-16-bold my-3">
            {tInventory("chooseFolder", { count: stockItems.length })}
          </p>
          <InputLabel>{tInventory("folderName")}</InputLabel>
          <FormControl className="w-full">
            <Select
              key={groups.length}
              defaultValue={groups[0]?.id}
              onChange={(ev) => handleGroupId(ev.target.value)}
              className="w-full"
            >
              {groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.isPrivate ? tInventory("internalStockLabel") : tInventory("forSaleLabel")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions className="flex mt-2">
        <Button variant="text" className="w-full" onClick={onClose}>
          {tCommon("buttons.cancel")}
        </Button>

        {groupName === tInventory("forSaleLabel") ? (
          <Button form="form" type="submit" variant="contained" className="w-full" onClick={onSubmit}>
            {tInventory("moveToFolder", { count: stockItems.length })}
          </Button>
        ) : (
          <Button
            variant="contained"
            className="w-full"
            onClick={() =>
              confirm({
                title: tInventory("moveToFolder", { count: stockItems.length }),
                text: tInventory("moveConfirmText"),
                confirmButton: tInventory("yesMove"),
                closeButton: tCommon("common.no"),
                severity: "error",
                onConfirm: () => {
                  onSubmit();
                }
              })
            }
          >
            {tInventory("moveToFolder", { count: stockItems.length })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MoveStockItemDialog;
